import { useEffect, useState } from 'react';
import { X, Loader2, Mail, Printer } from 'lucide-react';
import * as api from '../../lib/api';
import { formatCents, formatDate } from '../../lib/format';

const KIND_LABELS = {
  rent: 'Rent', proration: 'Proration', admin_fee: 'Admin fee',
  late_fee: 'Late fee', deposit: 'Deposit', credit: 'Credit',
};

/**
 * One invoice, in enough detail to answer a question about it on the phone.
 *
 * There is no mailer, so "email this" cannot send from the server. A mailto:
 * hands the whole thing to whatever mail client the admin already uses, with
 * the customer's address and a plain-text copy of the invoice filled in. It
 * sends from a real person's address, which for a billing query is arguably
 * better than a noreply anyway.
 */
export default function InvoiceDetail({ invoiceId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    api.adminInvoice(invoiceId)
      .then((payload) => { if (live) setData(payload); })
      .catch((err) => { if (live) setError(err.message); });
    return () => { live = false; };
  }, [invoiceId]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4 print:static print:bg-transparent print:p-0"
         role="dialog" aria-modal="true" aria-labelledby="invoice-title">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl print:max-h-none print:shadow-none">
        {!data && !error && (
          <div className="grid place-items-center py-16" role="status" aria-label="Loading invoice">
            <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
          </div>
        )}
        {error && <p role="alert" className="rounded-lg bg-rose-50 p-4 text-sm text-rose-800">{error}</p>}

        {data && <Body {...data} onClose={onClose} />}
      </div>
    </div>
  );
}

function Body({ invoice, lines, payments, onClose }) {
  const balance = Number(invoice.balance_cents);

  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 id="invoice-title" className="text-lg font-extrabold tracking-tight text-slate-900">
            {invoice.invoice_number}
          </h2>
          <p className="mt-0.5 text-sm text-slate-600">
            {invoice.facility_name} · space {invoice.unit_number} · {invoice.lease_number}
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close"
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 print:hidden">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 rounded-xl bg-slate-50 p-4 text-sm sm:grid-cols-4">
        <Field label="Billed to" value={`${invoice.first_name} ${invoice.last_name}`} />
        <Field label="Period" value={`${formatDate(invoice.period_start)} – ${formatDate(invoice.period_end)}`} />
        <Field label="Issued" value={formatDate(invoice.issued_on)} />
        <Field label="Due" value={formatDate(invoice.due_on)} />
      </dl>

      <table className="mt-5 w-full text-sm">
        <thead className="border-b border-slate-200 text-left text-[12px] font-bold uppercase tracking-wide text-slate-500">
          <tr><th className="pb-2">Item</th><th className="pb-2 text-right">Amount</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lines.map((line, i) => (
            <tr key={`${line.kind}-${i}`}>
              <td className="py-2">
                <span className="font-semibold text-slate-900">{KIND_LABELS[line.kind] ?? line.kind}</span>
                <span className="block text-xs text-slate-500">
                  {line.description}
                  {!line.is_taxable && ' · not taxed'}
                </span>
              </td>
              <td className="py-2 text-right tabular-nums text-slate-900">{formatCents(line.amount_cents)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t border-slate-200 text-sm">
          <tr><td className="pt-2 text-slate-600">Subtotal</td>
              <td className="pt-2 text-right tabular-nums">{formatCents(invoice.subtotal_cents)}</td></tr>
          <tr><td className="text-slate-600">Tax ({invoice.tax_rate_pct}%)</td>
              <td className="text-right tabular-nums">{formatCents(invoice.tax_cents)}</td></tr>
          <tr className="font-bold text-slate-900">
              <td className="pt-1">Total</td>
              <td className="pt-1 text-right tabular-nums">{formatCents(invoice.total_cents)}</td></tr>
          <tr><td className="text-slate-600">Paid</td>
              <td className="text-right tabular-nums text-emerald-700">−{formatCents(invoice.paid_cents)}</td></tr>
          <tr className={`text-base font-extrabold ${balance > 0 ? 'text-rose-700' : 'text-slate-900'}`}>
              <td className="pt-1">{balance > 0 ? 'Still owed' : 'Settled'}</td>
              <td className="pt-1 text-right tabular-nums">{formatCents(balance)}</td></tr>
        </tfoot>
      </table>

      {payments.length > 0 && (
        <>
          <h3 className="mt-6 text-xs font-bold uppercase tracking-wide text-slate-500">Payments</h3>
          <ul className="mt-2 space-y-1.5 text-sm">
            {payments.map((p) => (
              <li key={p.public_id} className="flex items-center justify-between gap-3">
                <span className="text-slate-600">
                  {formatDate(String(p.paid_at).slice(0, 10))} · {p.method ?? 'payment'}
                  {Number(p.refunded_cents) > 0 && (
                    <span className="ml-1 text-rose-700">
                      ({formatCents(p.refunded_cents)} refunded)
                    </span>
                  )}
                </span>
                <span className="font-semibold tabular-nums text-slate-900">{formatCents(p.net_cents)}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="mt-6 flex flex-wrap gap-2 print:hidden">
        <a href={mailtoFor(invoice, lines)}
           className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-bold text-white hover:bg-brand-700">
          <Mail className="h-4 w-4" aria-hidden="true" /> Email to {invoice.first_name}
        </a>
        <button type="button" onClick={() => window.print()}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50">
          <Printer className="h-4 w-4" aria-hidden="true" /> Print or save as PDF
        </button>
      </div>
      <p className="mt-2 text-xs text-slate-500 print:hidden">
        Opens your own mail client — nothing is sent from here until a mail server is set up.
      </p>
    </>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-[12px] font-bold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

/**
 * A plain-text copy of the invoice in a mailto: body.
 *
 * Everything is encodeURIComponent'd, including the line items — an invoice
 * description is free text an admin typed, and an unescaped newline or
 * ampersand in a mailto URL silently truncates the body at that point.
 */
function mailtoFor(invoice, lines) {
  const money = (cents) => `$${(Number(cents) / 100).toFixed(2)}`;
  const body = [
    `Hello ${invoice.first_name},`,
    '',
    `Here is invoice ${invoice.invoice_number} for space ${invoice.unit_number} at ${invoice.facility_name}.`,
    '',
    `Period:  ${invoice.period_start} to ${invoice.period_end}`,
    `Due:     ${invoice.due_on}`,
    '',
    ...lines.map((l) => `  ${(KIND_LABELS[l.kind] ?? l.kind).padEnd(12)} ${money(l.amount_cents)}`),
    `  ${'Tax'.padEnd(12)} ${money(invoice.tax_cents)}`,
    `  ${'Total'.padEnd(12)} ${money(invoice.total_cents)}`,
    `  ${'Paid'.padEnd(12)} ${money(invoice.paid_cents)}`,
    `  ${'Owed'.padEnd(12)} ${money(invoice.balance_cents)}`,
    '',
    invoice.phone ? `Any questions, call us on ${invoice.phone}.` : '',
    '',
    invoice.facility_name,
    `${invoice.address_line1}, ${invoice.city}, ${invoice.state_code} ${invoice.postal_code}`,
  ].filter((line) => line !== null).join('\n');

  const subject = `Invoice ${invoice.invoice_number} — ${invoice.facility_name}`;
  return `mailto:${encodeURIComponent(invoice.customer_email)}`
    + `?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
